import React, { useState } from 'react';
import type { AiModelConfig, AiModelOption, AiTestProject } from './types';
import { createTesterRun } from './api';
import ConceptTargetPicker, { type ConceptTargetSelection } from './ConceptTargetPicker';
import ModelPicker from './ModelPicker';

interface VisualEditFormProps {
  projects: AiTestProject[];
  models: AiModelOption[];
  onRunStarted: () => Promise<void> | void;
}

const VisualEditForm: React.FC<VisualEditFormProps> = ({
  projects,
  models,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [target, setTarget] = useState<ConceptTargetSelection | null>(null);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({});
  const [promptInline, setPromptInline] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const complete = Boolean(target?.combination.attributes_complete);
  const canRun = Boolean(
    project
    && target
    && complete
    && target.artifact
    && target.visual
    && promptInline.trim()
  );

  const handleRun = async () => {
    if (!project || !target?.artifact || !target.visual) {
      setError('Select a project, written concept, and visual first.');
      return;
    }
    if (!target.combination.attributes_complete) {
      setError(
        `Select an option for every attribute first. Missing: ${
          target.combination.missing_attributes.join(', ') || 'attributes'
        }.`
      );
      return;
    }
    const inline = promptInline.trim();
    if (!inline) {
      setError('Describe the edit in the prompt field.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: 'visual_concept_edit',
        blueprint_id: target.blueprint.id,
        tab_id: target.tab.id,
        combination_id: target.combination.id,
        artifact_id: target.artifact.id,
        visual_id: target.visual.id,
        model_id: modelId || undefined,
        model_config: modelConfig,
        prompt_inline: inline,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value ? Number(e.target.value) : '');
            setTarget(null);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <ConceptTargetPicker
        projectId={projectId}
        value={target}
        onChange={setTarget}
        requireArtifact
        artifactLabel="Concept tab"
        requireVisual
        disabled={submitting}
      />

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Edit prompt
        </label>
        <textarea
          value={promptInline}
          onChange={(e) => setPromptInline(e.target.value)}
          disabled={submitting}
          rows={5}
          placeholder="Describe how to change the selected visual. This is not saved as a prompt template."
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        />
      </div>

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey="visual_concept_edit"
        disabled={submitting}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !canRun}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Starting…' : 'Run Visual Edit'}
      </button>
    </div>
  );
};

export default VisualEditForm;
